import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

export const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const uid = searchParams.get('uid');
  const token = searchParams.get('token');

  const source = searchParams.get('source');
  const isMigration = source === 'migration';

  // If uid+token are present, show the confirm (set new password) form
  // Otherwise, show the request (enter email/username) form
  if (uid && token) {
    return <ConfirmResetForm uid={uid} token={token} isMigration={isMigration} />;
  }
  return <RequestResetForm />;
};

function RequestResetForm(): React.ReactElement {
  const [identifier, setIdentifier] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await apiClient.post('/api/users/request-password-reset/', { identifier });
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const inputClassName =
    'w-full px-4 py-2 bg-dark-bg border border-dark-border rounded focus:outline-none focus:border-bull-blue text-gray-100';

  return (
    <div className="min-h-screen flex items-center justify-center bg-dark-bg px-4">
      <div className="max-w-md w-full bg-dark-surface rounded-lg shadow-xl p-8">
        <h2 className="text-3xl font-bold text-center mb-2 text-gray-100">
          FZN Trading
        </h2>
        <p className="text-center text-sm text-gray-400 mb-8">Reset your password</p>

        {submitted ? (
          <div className="text-center">
            <div className="bg-bull-blue/10 border border-bull-blue/30 text-bull-blue px-4 py-3 rounded mb-6 text-sm">
              If an account exists with that email or username, a password reset link has been sent.
            </div>
            <p className="text-sm text-gray-400 mb-4">
              Check your email and click the link to set a new password.
            </p>
            <Link to="/login" className="text-bull-blue hover:underline text-sm">
              Back to sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-bear-red/10 border border-bear-red text-bear-red px-4 py-3 rounded text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="identifier" className="block text-sm font-medium text-gray-300 mb-2">
                Email or Username
              </label>
              <input
                id="identifier"
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                className={inputClassName}
                required
                disabled={isLoading}
                autoFocus
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-bull-blue hover:bg-green-600 disabled:bg-gray-600 text-white font-medium py-3 rounded transition-colors"
            >
              {isLoading ? 'Sending...' : 'Send Reset Link'}
            </button>

            <p className="text-center text-sm text-gray-400">
              <Link to="/login" className="text-bull-blue hover:underline">
                Back to sign in
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

/**
 * Shape of POST /api/users/confirm-password-reset/.
 *
 * The endpoint establishes the session itself (Django `login()`) and returns the
 * serialized user; it does NOT return a token — token auth was removed in
 * migration `users/0017_purge_authtoken`. Only the fields this page uses are
 * declared.
 */
interface ConfirmResetResponse {
  username: string;
}

function ConfirmResetForm({ uid, token, isMigration }: { uid: string; token: string; isMigration: boolean }): React.ReactElement {
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== newPasswordConfirm) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);

    let username: string;

    // Phase 1 — the reset itself. Only failures HERE may report that the
    // password was not changed.
    try {
      const response = await apiClient.post<ConfirmResetResponse>(
        '/api/users/confirm-password-reset/',
        { uid, token, new_password: newPassword, new_password_confirm: newPasswordConfirm },
      );
      username = response.username;
    } catch (err: unknown) {
      const error = err as { response?: { status?: number; data?: Record<string, unknown> } };
      const data = error.response?.data;

      if (data && typeof data === 'object') {
        // DRF validation errors
        const messages = Object.entries(data)
          .map(([field, errors]) => {
            const msgs = Array.isArray(errors) ? errors.join(', ') : String(errors);
            return field === 'non_field_errors' ? msgs : `${field}: ${msgs}`;
          })
          .join('\n');
        setError(messages);
      } else {
        setError('Failed to reset password. The link may be invalid or expired.');
      }
      setIsLoading(false);
      return;
    }

    // Phase 2 — the password IS changed and committed at this point, and the
    // emailed link is now spent (Django's token generator seeds on the password
    // hash). A failure below must never say the reset failed: the user would
    // retry with the old password, re-use a dead link, and conclude the reset
    // is broken. Send them to sign in instead.
    try {
      await login({ username, password: newPassword });
      navigate('/', { replace: true });
    } catch {
      navigate(`/login?reset=success&username=${encodeURIComponent(username)}`, {
        replace: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const inputClassName =
    'w-full px-4 py-2 bg-dark-bg border border-dark-border rounded focus:outline-none focus:border-bull-blue text-gray-100';

  return (
    <div className="min-h-screen flex items-center justify-center bg-dark-bg px-4">
      <div className="max-w-md w-full bg-dark-surface rounded-lg shadow-xl p-8">
        <h2 className="text-3xl font-bold text-center mb-2 text-gray-100">
          FZN Trading
        </h2>
        <p className="text-center text-sm text-gray-400 mb-2">
          {isMigration ? 'Welcome to the new FZN Trading' : 'Set a new password'}
        </p>
        {isMigration && (
          <p className="text-center text-xs text-gray-500 mb-6">
            Your Wall St. Jesus account is ready. Set a password below to log in.
          </p>
        )}
        {!isMigration && <div className="mb-6" />}

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-bear-red/10 border border-bear-red text-bear-red px-4 py-3 rounded whitespace-pre-line text-sm">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-300 mb-2">
              New Password
            </label>
            <input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className={inputClassName}
              required
              disabled={isLoading}
              autoFocus
            />
          </div>

          <div>
            <label htmlFor="newPasswordConfirm" className="block text-sm font-medium text-gray-300 mb-2">
              Confirm New Password
            </label>
            <input
              id="newPasswordConfirm"
              type="password"
              value={newPasswordConfirm}
              onChange={(e) => setNewPasswordConfirm(e.target.value)}
              className={inputClassName}
              required
              disabled={isLoading}
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-bull-blue hover:bg-green-600 disabled:bg-gray-600 text-white font-medium py-3 rounded transition-colors"
          >
            {isLoading ? 'Resetting...' : isMigration ? 'Set Password & Log In' : 'Reset Password'}
          </button>

          <p className="text-center text-sm text-gray-400">
            <Link to="/login" className="text-bull-blue hover:underline">
              Back to sign in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
